import { defineMessages } from 'react-intl';

/**
 * i18n messages for the shared Rovo Space starter prompts
 * Consumed via `buildSpaceStarters` / `useSpaceStarters` in `../starters`.
 */
const messages: {
	// --- Skills ---
	getDailyBriefTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	getProjectOverviewTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	// --- Chat prompts ---
	findMyActionItemsTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	findMyActionItemsPrompt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	prepForCheckinTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	prepForCheckinPrompt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	// --- Skills ---

	getDailyBriefTitle: {
		id: 'ai-mate.rovo-spaces.starters.get-daily-brief-title.v2.nonFinal',
		defaultMessage: 'Get workstream brief',
		description: 'Title of the get workstream brief skill action shown in Rovo empty chat state',
	},
	getProjectOverviewTitle: {
		id: 'ai-mate.rovo-spaces.starters.get-project-overview-title.nonFinal',
		defaultMessage: 'Get space overview',
		description: 'Title of the get space overview skill action shown in Rovo empty chat state',
	},

	// --- Chat prompts ---

	findMyActionItemsTitle: {
		id: 'ai-mate.rovo-spaces.starters.find-my-action-items-title.nonFinal',
		defaultMessage: 'Find my action items',
		description: 'Title of the find my action items chat starter shown in Rovo empty chat state',
	},
	findMyActionItemsPrompt: {
		id: 'ai-mate.rovo-spaces.starters.find-my-action-items-prompt',
		defaultMessage: `Write a list of Reminders for me for this workstream capturing what's new from the last few days. Scope & relevance rules:
- Consider all 1P & 3P resources as context associated to this project, even if not explicitly linked.
- Start from this object as the root. You may follow at most one hop of links from these pages (e.g., linked Jira issues, Confluence pages, JPD ideas, or Slack threads) only if the linked resource clearly refers back to this project (by name, ID, or direct context)
- A reminder qualifies only if it directly advances this workstream. Exclude items from other workstreams that merely share a meeting or author. Formatting rules: Format it for fast scanability
- H2. Reminders
- List only action items I am responsible for, if any.
- Find any actions I need to follow up on. Could be actual tasks assigned to me, comments I'm tagged in but haven't responded to, or things I said I would do in a conversation or meeting.
- Each reminder MUST start with the ballot box with tick emoji (☑️) and MUST NOT be a bulleted list.
- For each reminder, use:
☑️ Action (short)
When: (date/timeframe if known)
Context: (1 sentence)
Link: (URL) General rules:
- Use markdown headings and bolding for structure.
- Include links wherever relevant.
- If there are no reminders, write "No new reminders."`,
		description:
			'Prompt sent to Rovo Chat when the user clicks "Find my action items" on a space surface. Do not translate the formatting instructions or emoji characters.',
	},
	prepForCheckinTitle: {
		id: 'ai-mate.rovo-spaces.starters.prep-for-checkin-title.nonFinal',
		defaultMessage: 'Prep for check-in meeting',
		description:
			'Title of the prep for check-in meeting chat starter shown in Rovo empty chat state',
	},
	prepForCheckinPrompt: {
		id: 'ai-mate.rovo-spaces.starters.prep-for-checkin-prompt',
		defaultMessage: `I have a weekly sync starting soon for this workstream. Prep a brief agenda, including:
1. Highlights from the last week. Include 1-4. Can cover momentum, milestones, decisions, risks, learnings. Use emoji for each.
2. Open items or discussion topics that need to be closed out. Use emoji to mark priority.
3. An empty section to capture notes, action items, and decisions. Add a table with one empty row to get the user started.`,
		description:
			'Prompt sent to Rovo Chat when the user clicks "Prep for check-in meeting" on a space surface. Do not translate the formatting instructions or emoji characters.',
	},
});

export default messages;
